/**
 * Helper for passes that need to fill the viewport with a single quad.
 * @module
 */
import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { checkWebGLContextLost } from "../../../higharc/checkWebGLContextLost";

const vec4 = new THREE.Vector4();

export class FullScreenQuad {
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private geometry = new THREE.PlaneGeometry(2, 2);

  private mesh: THREE.Mesh;

  constructor(material?: THREE.Material) {
    this.mesh = new THREE.Mesh(this.geometry, material);
  }

  get material(): THREE.Material {
    return this.mesh.material as THREE.Material;
  }

  set material(value: THREE.Material) {
    this.mesh.material = value;
  }

  render(renderer: THREE.WebGLRenderer) {
    this.mesh.scale.set(1, 1, 1);
    this.mesh.position.set(0, 0, 0);
    this.mesh.updateMatrixWorld();
    try {
      renderer.render(this.mesh, this.camera);
    } catch (e) {
      checkWebGLContextLost(renderer, e, "Rendering full-screen effect failed.");
    }
  }

  renderPixelRectangle(renderer: THREE.WebGLRenderer, aabb: AABB2) {
    const { minX, minY, maxX, maxY } = aabb;
    renderer.getViewport(vec4);
    const viewportWidth = vec4.z;
    const viewportHeight = vec4.w;
    this.mesh.scale.set((maxX - minX) / viewportWidth, (maxY - minY) / viewportHeight, 1);
    this.mesh.position.set(
      (((minX + maxX) * 0.5) / viewportWidth) * 2 - 1,
      (((minY + maxY) * 0.5) / viewportHeight) * 2 - 1,
      0,
    );
    this.mesh.updateMatrixWorld();
    try {
      renderer.render(this.mesh, this.camera);
    } catch (e) {
      checkWebGLContextLost(renderer, e, "Rendering full-screen effect failed.");
    }
  }
}
